import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { and } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import HotelRatesTablesCellsThHotel from 'lumen/components/hotel/rates/tables/cells/th-hotel';
import ParityTablesCellsTdDate from 'lumen/components/parity/tables/cells/td-date';
import ParityTablesCellsTdLossOnMeta from 'lumen/components/parity/tables/cells/td-loss-on-meta';
import ParityTablesCellsTdLowestOnMeta from 'lumen/components/parity/tables/cells/td-lowest-on-meta';
import ParityTablesCellsTdParity from 'lumen/components/parity/tables/cells/td-parity';
import type { Period } from 'lumen/models';
import type { HotelOrMarket } from 'lumen/services/hotel-and-market';
import type { ParityType } from 'lumen/services/liveupdate';
import {
  COMPONENT_DOMAINS,
  RATES_OUT_OF_SYNC_THRESHOLD,
} from 'lumen/utils/constants/general-constants';

const COLUMNS = {
  DATE: 'date',
  LOWEST_RATE_METASEARCH: 'marketotb',
  LOSS_CHANNELS_METASEARCH: 'market-demand',
};

interface ParityTablesTableParitySignature {
  Args: {
    availableOTAs: Ota[];
    hasDayLiveShop: boolean;
    hotel?: HotelOrMarket;
    isLoading: boolean;
    isModal?: boolean;
    isParityAnalytics?: boolean;
    message: {
      message: string;
      showOverlay: boolean;
    };
    parityType: ParityType;
    periods: Period[] | null;
    showMemberRate: boolean;
    showMeta: boolean;
  };
}

export default class ParityTablesTableParity extends Component<ParityTablesTableParitySignature> {
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('general.date'),
        valuePath: COLUMNS.DATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdDate,
        minWidth: 120,
        maxWidth: 120,
        textAlign: 'left',
        isFixed: 'left',
        hasRightBorder: true,
        hasDayLiveShop: this.args.hasDayLiveShop,
        parityType: this.args.parityType,
        isParityAnalytics: this.isParityAnalytics,
        hotel: this.hotel,
      },
    ];

    this.filteredMajorOTAs.forEach((ota, index) => {
      columns.push({
        name: ota.name,
        valuePath: ota.id!,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdParity,
        textAlign: 'right',
        minWidth: 130,
        maxWidth: 130,
        otaId: ota.id!,
        hasRightBorder: this.filteredMajorOTAs.length - 1 === index,
        componentDomain: COMPONENT_DOMAINS.PARITY,
        isDisconnected: this.disconnectedOTAs
          .map((disconnectedOta) => disconnectedOta.id)
          .includes(ota.id),
        isParityOtaOutOfSync: this.outOfSyncParityOTAs[ota.id!],
        isParityAnalytics: this.isParityAnalytics,
        isModal: this.args.isModal,
        hotel: this.hotel,
        showMemberRate: this.args.showMemberRate,
      });
    });

    if (this.args.showMeta) {
      columns.push({
        name: this.intl.t('parity.loss_channels_on_metasearch'),
        valuePath: COLUMNS.LOSS_CHANNELS_METASEARCH,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdLossOnMeta,
        availableOTAs: this.args.availableOTAs,
        otaId: 'others',
        textAlign: 'left',
        minWidth: 220,
        maxWidth: 300,
        componentDomain: COMPONENT_DOMAINS.PARITY_META,
        isParityAnalytics: this.isParityAnalytics,
        isModal: this.args.isModal,
        hotel: this.hotel,
      });

      columns.push({
        name: this.intl.t('rates.lowest_rate'),
        valuePath: COLUMNS.LOSS_CHANNELS_METASEARCH,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdLowestOnMeta,
        textAlign: 'right',
        otaId: 'others',
        minWidth: 120,
        maxWidth: 120,
        componentDomain: COMPONENT_DOMAINS.PARITY_META,
        isParityAnalytics: this.isParityAnalytics,
        isModal: this.args.isModal,
        hasRightBorder: true,
        hotel: this.hotel,
        hasMargin: false,
      });
    }

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disconnectedOTAs() {
    return this.args.availableOTAs.filter(
      (availableOTA) =>
        availableOTA.isConnected !== undefined && !availableOTA.isConnected,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredMajorOTAs() {
    return this.args.availableOTAs.filter(
      (availableOTA) => availableOTA['isMajor'],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.args.hotel ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isParityAnalytics() {
    return this.args.isParityAnalytics ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPolling() {
    return this.liveupdate.isPolling;
  }

  /**
   * Creates an object with a key for each OTA in the model and as value true/false whether or not the OTA is considered out of sync.
   * Example: { bookingdotcom: true, expedia: false, agoda: false }
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get outOfSyncParityOTAs() {
    if (this.args.isLoading || this.isPolling) {
      return {};
    }
    const totalRates: Record<string, number | boolean> = {};
    const totalOutOfSync: Record<string, number> = {};

    this.args.periods?.forEach((period) => {
      if (period.parities) {
        period.parities.forEach((parity) => {
          if (parity.channel in totalRates) {
            (totalRates[parity.channel] as number) += 1;
            (totalOutOfSync[parity.channel] as number) += parity.is_out_of_sync
              ? 1
              : 0;
          } else {
            totalRates[parity.channel] = 1;
            totalOutOfSync[parity.channel] = parity.is_out_of_sync ? 1 : 0;
          }
        });
      }
    });

    return Object.keys(totalRates).reduce(
      (result, currentOta) => {
        let isCurrentOtaOutOfSync = false;
        if (totalRates[currentOta] !== 0) {
          isCurrentOtaOutOfSync =
            (totalOutOfSync[currentOta] as number) /
              (totalRates[currentOta] as number) >=
            RATES_OUT_OF_SYNC_THRESHOLD;
        }
        result[currentOta] = isCurrentOtaOutOfSync;
        return result;
      },
      {} as Record<string, boolean>,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rows: CustomEmberTableRow[] = [];
    this.args.periods?.forEach((period) => {
      const row = {
        [COLUMNS.DATE]: period,
        [COLUMNS.LOWEST_RATE_METASEARCH]: period,
        [COLUMNS.LOSS_CHANNELS_METASEARCH]: period,
      };

      this.filteredMajorOTAs.forEach((majorOTA) => {
        // @ts-expect-error: Type 'null' cannot be used as an index type.
        row[majorOTA.id] = period;
      });

      rows.push(row);
    });

    return rows;
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @isLoading={{@isLoading}}
      @overlayMessage={{if
        (and @message.showOverlay @message.message)
        @message.message
      }}
      @removeTableBorder={{@isModal}}
      @rows={{this.rows}}
      @widthConstraint="gte-container-slack"
    />
  </template>
}
